/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { BankStatementWithAdditionalData, FI_CODE } from "models/bank.model";
import {
  COL_BANKSTATEMENT_LABEL_DATA,
  ReviewBankStatementColIdType,
} from "pages/Review/column.data";
import { PALETTE } from "styles/base/variables";
import { convertCurrency, convertDate } from "utils/converter";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useEffect, useRef, useState } from "react";
import { MultiAccountCodeAmount, UpdateTxData } from "models/business.model";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import { updateTransaction } from "pages/remotes";
import { useLocation } from "react-router-dom";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import { bankAccountNoPipe } from "pipes/bank.pipe";

interface MultiAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  data: BankStatementWithAdditionalData;
  subjectList: { value: string; label: string }[];
  successCallback?: () => void;
}

const displayDataKeys: ReviewBankStatementColIdType[] = [
  "trDtTm", // 거래일시
  "dealType", // 입출금여부
  "bankCd", // 은행분류
  "accNo", // 계좌번호
  "trTp", // 구분
  "trNm", // 적요
  "storeName", // 거래처명
  "userMemo", // 사용자메모
  "balance", // 계좌 잔액
  "amount", // 총 금액
  // "accountCode", // 계정코드
  // "accountName", // 계정과목명
];

const iconButtonHoverPrimaryStyle = css`
  &:hover {
    background-color: ${PALETTE.primaryHover};
    svg {
      color: ${PALETTE.primary};
    }
  }
`;

function MultiAccountDialog({
  isOpen,
  onClose,
  data,
  subjectList,
  successCallback = () => {},
}: MultiAccountDialogProps) {
  const location = useLocation();
  const locationBizId = location.pathname.split("/")?.[3];

  const inputSubjectRef = useRef<HTMLInputElement>(null);
  const inputAmountRef = useRef<HTMLInputElement>(null);
  const optionSubjectRef = useRef<HTMLUListElement>(null);

  const displayDataList: {
    key: ReviewBankStatementColIdType;
    label: string;
    value: string | React.ReactNode | any; // TODO: type 수정 필요ㄴ
  }[] = displayDataKeys.map((key) => {
    let value = data[key as keyof BankStatementWithAdditionalData] || "-";
    if (key === "trDtTm") value = convertDate(data.trDtTm) || "-";
    else if (key === "bankCd")
      value = FI_CODE[data.bankCd as keyof typeof FI_CODE];
    else if (key === "accNo")
      value = bankAccountNoPipe(data.accNo, data.bankCd) || "-";
    else if (key === "balance" || key === "amount")
      value =
        convertCurrency(
          data[key as keyof BankStatementWithAdditionalData] as number
        ) + "원" || "-";

    return {
      key,
      label: COL_BANKSTATEMENT_LABEL_DATA[key]!, // TODO: Handle undefined case - MultiAccountCodeAmount[] 때문에 발생
      value,
    };
  });

  const [accountDataList, setAccountDataList] = useState<
    MultiAccountCodeAmount[]
  >([]);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [accIdxUnderEdit, setAccIdxUnderEdit] = useState<number | null>(null);
  const [accAmount, setAccAmount] = useState<number>(0); // 현재 화면에 표기된 계정과목 금액 합산
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  useEffect(() => {
    if (data.multiAccountCodes) {
      setAccountDataList(data.multiAccountCodes);
      setAccAmount(
        // 멀티인 경우에만 계산 필요 (단일은 총액과 동일, 없을 경우 초기값 0 그대로)
        data.multiAccountCodes.reduce((sum, item) => sum + item.amount, 0)
      );
    } else if (data.accountCode) {
      setAccountDataList([
        {
          accountCode: data.accountCode,
          amount: data.amount || 0,
        },
      ]);
      setAccAmount(data.amount || 0);
    } else {
      setAccountDataList([]);
    }
  }, []);

  const openAddMode = () => {
    setIsOpenAdd(true);
  };

  const closeAddMode = () => {
    setIsOpenAdd(false);
  };

  const openEditMode = (index: number) => {
    setIsOpenEdit(true);
    setAccIdxUnderEdit(index);
  };

  const closeEditMode = () => {
    setIsOpenEdit(false);
    setIsOpenAdd(false);
    setAccIdxUnderEdit(null);
  };

  const submitSettings = () => {
    const sortedMAList = accountDataList.sort(
      // 금액 내림차순 정렬
      (a, b) => b.amount - a.amount
    );

    const tmpData: UpdateTxData = {
      id: data.id,
      accountCode: sortedMAList[0].accountCode, // 첫 번째 계정 코드(최대 금액)로 설정
      multiAccountCodes: sortedMAList,
    };

    updateTransaction("bank", locationBizId, [tmpData])
      .then((res) => {
        setIsAlertOpen(true);
        setAlertDialogData({
          message: "다중 계정 설정이 완료되었습니다.",
          type: "success",
          confirmFunc: onClose,
        });
        successCallback();
      })
      .catch((error) => {
        console.error("Error updating transaction:", error);
      });
  };

  const saveAccountCodeEdit = (index: number) => {
    const isAdd = index === accountDataList.length;

    const [currInputSbj, currInputAmt] = [
      inputSubjectRef.current,
      inputAmountRef.current,
    ];

    if (!(currInputSbj && currInputSbj.value)) {
      setIsAlertOpen(true);
      setAlertDialogData({
        message: "계정과목을 입력해주세요.",
        type: "error",
      });
      return;
    }

    if (
      !(
        currInputAmt &&
        currInputAmt.value &&
        !isNaN(parseFloat(currInputAmt.value))
      )
    ) {
      setIsAlertOpen(true);
      setAlertDialogData({
        message: "유효한 금액을 입력해주세요.",
        type: "error",
      });
      return;
    }

    setAccountDataList((prev) => {
      const newAccountDataList = [...prev];
      const subjectValue = currInputSbj?.value || "";
      const amountValue = parseFloat(currInputAmt?.value || "0");

      if (isAdd) {
        newAccountDataList.push({
          accountCode: subjectValue,
          amount: amountValue,
        });
        setAccAmount((prevAmt) => prevAmt + amountValue);
      } else {
        newAccountDataList[index] = {
          accountCode: subjectValue,
          amount: amountValue,
        };

        const prevAmount = prev[index].amount;
        setAccAmount((prevAmt) => prevAmt - prevAmount + amountValue);
      }

      return newAccountDataList;
    });

    escapeInput();
  };

  const deleteAccount = (index: number, accountCode: string) => {
    const message = `계정코드 ${accountCode}이 목록에서 제외되었습니다.\n설정 완료 후 해당 내용이 반영됩니다.`;

    setAlertDialogData({
      message: message,
      type: "warning",
      confirmFunc: () => {
        setAccountDataList((prev) => {
          const newAccountDataList = [...prev];
          const deletedAmount = newAccountDataList[index].amount;
          newAccountDataList.splice(index, 1);
          setAccAmount((prevAmount) => prevAmount - deletedAmount);
          return newAccountDataList;
        });
        setIsAlertOpen(false);
      },
    });
    setIsAlertOpen(true);
  };

  const escapeInput = () => {
    if (isOpenEdit) closeEditMode();
    if (isOpenAdd) closeAddMode();
  };

  const onKeyDownAmountInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // IME 입력 중인 경우 무시 (한글 조합으로 두번 호출 무시)
    if (e.key === "Enter") {
      e.preventDefault(); // 엔터키 입력 시 폼 제출 방지

      const subjectVal = inputSubjectRef.current?.value || "";
      if (subjectVal) {
        if (isOpenEdit && accIdxUnderEdit !== null)
          // idx === 0으로 인해 not null로 검증
          saveAccountCodeEdit(accIdxUnderEdit);
        else saveAccountCodeEdit(accountDataList.length);

        escapeInput();
      } else {
        inputSubjectRef.current?.focus(); // 계정과목 입력이 없을 경우 계정과목 인풋으로 포커스 이동
      }
    } else if (e.key === "Escape") {
      e.stopPropagation(); // 이스케이프 키 입력 시 이벤트 전파 방지 (다이얼로그 종료 방지)
      escapeInput(); // 다중계정 설정 종료
    }
  };

  const filterSelectedCodes = (
    dataArr: MultiAccountCodeAmount[]
  ): { value: string; label: string }[] => {
    const selectedCodes = dataArr.map((item) => item.accountCode);

    return subjectList.filter(
      (subject) => !selectedCodes.includes(subject.value)
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>다중계정 설정</DialogTitle>
      <DialogContent
        css={css`
          padding-bottom: 0;
        `}
      >
        <Divider
          css={css`
            margin-bottom: 16px;
          `}
        />
        <Grid container spacing={1} css={{ marginBottom: "16px" }}>
          {displayDataList.map((data) => (
            <Grid item xs={6} key={data.key}>
              <div
                css={css`
                  color: ${PALETTE.textMid};
                  font-size: 12px;
                  font-weight: 600;
                `}
              >
                {data.label}
              </div>{" "}
              <div
                css={css`
                  color: ${PALETTE.text};
                  font-size: 14px;
                  font-weight: 500;
                `}
              >
                {data.value || "-"}
              </div>
            </Grid>
          ))}
        </Grid>
        <Divider
          css={css`
            margin-bottom: 16px;
          `}
        />
        <Grid>
          {accountDataList.length > 0 ? (
            accountDataList.map(({ accountCode, amount }, index) => (
              <Grid container spacing={1} key={accountCode}>
                <Grid
                  item
                  xs={6}
                  display="flex"
                  alignItems="center"
                  fontWeight={600}
                >
                  {accIdxUnderEdit === index ? (
                    <SubjectInput
                      defatultValue={accountCode}
                      inputSubjectRef={inputSubjectRef}
                      optionSubjectRef={optionSubjectRef}
                      inputAmountRef={inputAmountRef}
                      subjectList={filterSelectedCodes(
                        accountDataList.filter((_, i) => i !== index)
                      )} // 본 계정의 계정과목은 필터링에서 제외
                      escape={escapeInput}
                    />
                  ) : (
                    subjectList.find((subject) => subject.value === accountCode)
                      ?.label || "없음"
                  )}
                </Grid>
                <Grid
                  item
                  xs={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  fontWeight={500}
                  color={PALETTE.text}
                >
                  {accIdxUnderEdit === index ? (
                    <>
                      <BaseInput
                        name="금액"
                        placeholder="금액 입력"
                        ref={inputAmountRef}
                        defaultValue={amount.toString()}
                        onKeyDown={onKeyDownAmountInput}
                        css={css`
                          width: 100px;
                          padding: 8px;
                          text-align: right;
                        `}
                      />
                      <IconButton
                        css={css`
                          margin-left: -4px;
                          margin-right: -8px;
                          transform: scale(0.8);
                        `}
                        onClick={closeEditMode}
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                      <IconButton
                        css={css`
                          margin-left: -8px;
                          margin-right: -10px;
                          transform: scale(0.8);
                          ${iconButtonHoverPrimaryStyle}
                        `}
                        onClick={() => saveAccountCodeEdit(index)}
                      >
                        <CheckOutlinedIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      {amount.toLocaleString()}원
                      {accountDataList.length > 1 ? ( // 단일 계정일 경우 삭제 불가
                        <IconButton
                          disabled={isOpenEdit || isOpenAdd}
                          css={css`
                            margin-left: -4px;
                            margin-right: -8px;
                            transform: scale(0.8);
                          `}
                          onClick={() => deleteAccount(index, accountCode)}
                        >
                          <CloseOutlinedIcon />
                        </IconButton>
                      ) : null}
                      <IconButton
                        disabled={isOpenEdit || isOpenAdd}
                        css={css`
                          margin-left: -8px;
                          margin-right: -10px;
                          transform: scale(0.7);
                        `}
                        onClick={() => openEditMode(index)}
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                    </>
                  )}
                </Grid>
              </Grid>
            ))
          ) : (
            <div>계정 코드가 없습니다.</div>
          )}
        </Grid>
        <Grid
          container
          display="flex"
          justifyContent="center"
          mt={1}
          mb={2}
          minHeight="40px"
        >
          {isOpenAdd ? (
            <>
              <Grid item xs={6}>
                <SubjectInput
                  subjectList={filterSelectedCodes(accountDataList)}
                  inputSubjectRef={inputSubjectRef}
                  optionSubjectRef={optionSubjectRef}
                  inputAmountRef={inputAmountRef}
                  escape={escapeInput}
                />
              </Grid>
              <Grid
                item
                xs={6}
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-start"
              >
                <BaseInput
                  name="금액"
                  placeholder="금액 입력"
                  ref={inputAmountRef}
                  defaultValue={
                    data.amount - accAmount > 0
                      ? (data.amount - accAmount).toString()
                      : "0"
                  }
                  css={css`
                    width: 100px;
                    padding: 8px;
                    text-align: right;
                  `}
                  onKeyDown={onKeyDownAmountInput}
                />
                <IconButton
                  css={css`
                    margin-left: -4px;
                    margin-right: -8px;
                    transform: translate(0, -3px) scale(0.8);
                  `}
                  onClick={closeAddMode}
                >
                  <CloseOutlinedIcon />
                </IconButton>
                <IconButton
                  css={css`
                    margin-left: -8px;
                    margin-right: -10px;
                    transform: translate(0, -3px) scale(0.8);
                    ${iconButtonHoverPrimaryStyle}
                  `}
                  onClick={() => saveAccountCodeEdit(accountDataList.length)}
                >
                  <CheckOutlinedIcon />
                </IconButton>
              </Grid>
            </>
          ) : isOpenEdit ? null : (
            <div>
              <Tooltip title="계정 추가" placement="right">
                <IconButton
                  disabled={isOpenEdit || isOpenAdd}
                  css={iconButtonHoverPrimaryStyle}
                  onClick={openAddMode}
                >
                  <AddOutlinedIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </Grid>
        <Grid
          p={1.5}
          display="flex"
          flexDirection="column"
          gap={1}
          alignItems="flex-end"
        >
          <Grid fontWeight={600} fontSize={14}>
            {data.amount - accAmount === 0 ? (
              <span
                css={css`
                  color: ${PALETTE.primary};
                `}
              >
                금액일치
              </span>
            ) : data.amount - accAmount > 0 ? (
              <span
                css={css`
                  color: ${PALETTE.textMid};
                `}
              >
                {(data.amount - accAmount)?.toLocaleString()}원 부족
              </span>
            ) : (
              <span
                css={css`
                  color: ${PALETTE.error};
                `}
              >
                {Math.abs(accAmount - data.amount)?.toLocaleString()}원 초과
              </span>
            )}
          </Grid>
        </Grid>
        <Divider />
      </DialogContent>
      <DialogActions
        css={css`
          padding: 16px 24px;
        `}
      >
        <BaseButton
          variant="outlined"
          color={PALETTE.primary}
          onClick={onClose}
        >
          취소
        </BaseButton>
        <Tooltip
          title={
            accAmount !== data.amount
              ? "계정과목 금액이 총액과 일치하지 않습니다."
              : ""
          }
        >
          <div>
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              disabled={isOpenAdd || isOpenEdit || accAmount !== data.amount}
              onClick={submitSettings}
            >
              설정
            </BaseButton>
          </div>
        </Tooltip>
      </DialogActions>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </Dialog>
  );
}

interface SubjectInputProps {
  inputSubjectRef: React.RefObject<HTMLInputElement>;
  optionSubjectRef: React.RefObject<HTMLUListElement>;
  inputAmountRef: React.RefObject<HTMLInputElement>;
  subjectList: { value: string; label: string }[];
  escape: () => void;
  defatultValue?: string;
}

const SubjectInput = ({
  inputSubjectRef,
  optionSubjectRef,
  inputAmountRef,
  subjectList,
  escape,
  defatultValue = "",
}: SubjectInputProps) => {
  const [isSubjectListOpen, setIsSubjectListOpen] = useState(false);
  const [filteredSubjectList, setFilteredSubjectList] =
    useState<{ value: string; label: string }[]>(subjectList);

  const onSetMatchedSubject = (value: string) => {
    if (!value) {
      inputSubjectRef.current!.value = "";
      return;
    }

    const matchedSubject = subjectList.find((subject) =>
      subject.label.includes(value)
    ); // 라벨에 계정코드도 있어서 라벨 검색으로 충분
    if (matchedSubject) {
      if (inputSubjectRef.current)
        inputSubjectRef.current.value = matchedSubject.value;
      if (inputAmountRef.current) inputAmountRef.current.focus(); // 계정과목 선택 후 금액 인풋으로 포커스 이동
    } else {
      inputSubjectRef.current!.value = "";
    }
    setIsSubjectListOpen(false); // 입력 후 리스트 닫기
    setFilteredSubjectList(subjectList); // 필터링 초기화
  };

  const onKeydownSearchSbj = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // IME 입력 중인 경우 무시 (한글 조합으로 두번 호출 무시)
    if (e.key === "Enter") {
      e.currentTarget.blur(); // 엔터키 입력 시 포커스 아웃 (blur > onBlur 호출) - tab과 동일한 작동
    } else if (e.key === "Escape") {
      e.stopPropagation(); // 이스케이프 키 입력 시 이벤트 전파 방지 (다이얼로그 종료 방지)
      setIsSubjectListOpen(false); // 리스트 닫기
      escape(); // 다중계정 설정 종료
    }
  };

  const onSearchSbjAll = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value || "";
    if (value) {
      setFilteredSubjectList(
        subjectList.filter((subject) => subject.label.includes(value))
      );
    } else {
      setFilteredSubjectList(subjectList);
    }
  };

  const onBlurSubjectInput = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (
      !relatedTarget ||
      relatedTarget?.tagName !== "LI" ||
      relatedTarget?.parentNode !== optionSubjectRef.current
    )
      setIsSubjectListOpen(false);

    onSetMatchedSubject(e.currentTarget.value); // 포커스 아웃 시에도 입력값에 맞는 계정과목 설정
  };

  const convertSubjectName = (value: string | undefined) => {
    if (!value) return "-";
    const matchedSubject = subjectList.find(
      (subject) => subject.value === value
    );

    const subjectName = matchedSubject
      ? matchedSubject.label?.split("(")?.[0]
      : "";
    return subjectName || "-";
  };

  return (
    <Grid display="flex" alignItems="center" columnGap={1.5}>
      <Grid width="136px" position="relative">
        <BaseInput
          name="계정과목"
          placeholder="계정과목 선택"
          ref={inputSubjectRef}
          defaultValue={defatultValue}
          css={css`
            width: 120px;
            padding: 8px;
          `}
          onFocus={() => setIsSubjectListOpen(true)}
          onBlur={onBlurSubjectInput}
          onKeyDown={onKeydownSearchSbj}
          onInput={onSearchSbjAll}
        />
        {isSubjectListOpen && filteredSubjectList.length > 0 && (
          <ul
            css={css`
              margin: 0;
              padding: 0;
              position: absolute;
              bottom: 0;
              left: 100%;
              width: 100%;
              max-height: 272px;
              overflow-y: auto;
              border-radius: 4px;
              box-sizing: border-box;
              border: 1px solid ${PALETTE.border};
              background-color: white;
              opacity: 0.8;
              z-index: 20;
              list-style: none;
            `}
            ref={optionSubjectRef}
          >
            {filteredSubjectList.map((subject) => (
              <li
                key={subject.value}
                tabIndex={-1}
                onMouseDown={() => {
                  // onClick은 onBlur보다 나중에 실행되지만, onMouseDown은 먼저 실행됨
                  inputSubjectRef.current!.value = subject.value;
                  setIsSubjectListOpen(false);
                }}
                css={css`
                  padding: 8px 12px;
                  cursor: pointer;
                  list-style: none;
                  font-size: 14px;

                  &:hover {
                    background-color: ${PALETTE.primaryHover};
                  }
                `}
              >
                {subject.label}
              </li>
            ))}
          </ul>
        )}
      </Grid>
      <Grid>
        <span
          css={css`
            color: ${PALETTE.textMid};
            font-size: 14px;
            font-weight: 500;
          `}
        >
          {/* 계정과목 선택 후 포커스 아웃 시에도 입력값에 맞는 계정과목 설정 */}
          {convertSubjectName(inputSubjectRef.current?.value)}
        </span>
      </Grid>
    </Grid>
  );
};

export default MultiAccountDialog;
